<template>
  <section class="card">
    <h2 class="section-heading">
      {{ t('employerForm.employer.title') }}
    </h2>
    <div class="form-grid">
      <div class="form_group">
        <label>{{ t('employerForm.employer.name') }}</label>

        <input type="text" v-model="form.name" :placeholder="t('employerForm.employer.name')"
          @input="clearError('employer.name')" :class="{ 'is-invalid': formErrors.has('employer.name') }" />

        <p class="invalid-feedback" v-if="formErrors.has('employer.name')">
          {{ t(formErrors.first('employer.name')) }}
        </p>
      </div>

      <div class="form_group">
        <label>{{ t('employerForm.employer.id') }}</label>

        <input type="text" v-model="form.id" :placeholder="t('employerForm.employer.id')" @input="validateEmployerId();"
          :class="{ 'is-invalid': formErrors.has('employer.id') }" />

        <p class="invalid-feedback" v-if="formErrors.has('employer.id')">
          {{ t(formErrors.first('employer.id')) }}
        </p>
      </div>

      <div class="form_group">
        <label>{{ t('employerForm.employer.phone') }}</label>

        <input type="tel"
          inputmode="numeric"
          v-model="form.phone"
          :placeholder="t('employerForm.employer.phone')"
          @input="onPhoneInput" 
          @keypress="allowPhoneCharacters"
          :class="{ 'is-invalid': formErrors.has('employer.phone') }" />

        <p class="invalid-feedback" v-if="formErrors.has('employer.phone')">
          {{ t(formErrors.first('employer.phone')) }}
        </p>
      </div>

      <div class="form_group">
        <label>{{ t('employerForm.employer.email') }}</label>

        <input type="email" v-model="form.email" :placeholder="t('employerForm.employer.email')"
          @input="clearError('employer.email')" :class="{ 'is-invalid': formErrors.has('employer.email') }" />

        <p class="invalid-feedback" v-if="formErrors.has('employer.email')">
          {{ t(formErrors.first('employer.email')) }}
        </p>
      </div>

      <div class="form_group full-width">
        <label>{{ t('employerForm.employer.preferredCommunication') }}</label>

        <div class="radio_group">
          <label>
            <input type="radio" v-model="form.preferredCommunication" value="email" />
            {{ t('employerForm.employer.communicationEmail') }}
          </label>

          <label>
            <input type="radio" v-model="form.preferredCommunication" value="post" />
            {{ t('employerForm.employer.communicationPost') }}
          </label>
        </div>
      </div>

      <div class="form_group full-width" v-if="form.preferredCommunication === 'post'">
        <label>{{ t('employerForm.employer.address') }}</label>

        <input type="text" v-model="form.address" :placeholder="t('employerForm.employer.address')"
          @input="clearError('employer.address')" :class="{ 'is-invalid': formErrors.has('employer.address') }" />

        <p class="invalid-feedback" v-if="formErrors.has('employer.address')">
          {{ t(formErrors.first('employer.address')) }}
        </p>
      </div>
    </div>
  </section>
</template>

<script setup>
import Error from '@/utils/Error';
import { allowPhoneCharacters, sanitizePhoneNumber } from '@/utils/helpers';
import { reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({

    })
  },

  formErrors: {
    type: Object,
    required: false,
    default: reactive(new Error({}))
  }
});

const validateEmployerId = () => {
  clearError('employer.id');
  if (!form.id) {
    return;
  }

  if (!isValidIsraeliID(form.id)) {
    setError('employer.id', 'validation.employer.id.format')
  }
};
const isValidIsraeliID = (id) => {
  id = String(id || '').trim();
  if (!/^\d{1,9}$/.test(id)) {
    return false;
  }
  id = id.padStart(9, '0');
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    let val = Number(id[i]) * ((i % 2) + 1);
    if (val > 9) {
      val -= 9;
    }
    sum += val;
  }
  return sum % 10 === 0;
};

const emit = defineEmits([
  'update:modelValue',
  'removeError',
  'setError'
]);

const form = reactive({
  ...props.modelValue
});

watch(
  form,
  (val) => emit('update:modelValue', val),
  { deep: true }
);

watch(
  () => props.modelValue,
  (newValue) => {
    Object.assign(form, newValue);
  },
  { deep: true, immediate: true }
);

const clearError = (name) => {
  emit('removeError', name);
};
const setError = (key, value) => {
  emit('setError', {
    key: key,
    message: value
  });
}
const onPhoneInput = (event) => {
  form.phone = sanitizePhoneNumber(event.target.value);
  clearError('employer.phone');
};
</script>

<style scoped>
.full-width {
  grid-column: span 2;
}
.radio_group {
  display: flex;
  gap: 1.5rem;
  margin-top: 0.5rem;
}

.radio_group label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-weight: normal;
  cursor: pointer;
}

.radio_group input[type="radio"] {
  cursor: pointer;
  width: auto;
  flex: 0 0 auto;
}
</style>